import { Deferred, Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { AgencyCandidatesToContactSection } from "@/components/agency/agency-candidates-to-contact-section"
import { AgencyCandidatesToFollowUpSection } from "@/components/agency/agency-candidates-to-follow-up-section"
import { AgencyCandidatesToReviewSection } from "@/components/agency/agency-candidates-to-review-section"
import { AgencyInvitesQuietSection } from "@/components/agency/agency-invites-quiet-section"
import { AgencySubmissionsToReviewSection } from "@/components/agency/agency-submissions-to-review-section"
import { CandidatesAwaitingBrandSection } from "@/components/candidates/candidates-awaiting-brand-section"
import { HomeTriageSection } from "@/components/home-triage/home-triage-section"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useRetainedDeferredSection } from "@/hooks/use-retained-deferred-section"
import AppLayout from "@/layouts/app-layout"
import type { AgencyHomeProps } from "@/types/agency-home"

/**
 * OKS-708 — Agency manager home (`/agency`).
 *
 * The two shipped triage sections (OKS-706 Candidates-to-review + OKS-707
 * Submissions-to-review), with a brand badge on every card.
 *
 * OKS-854 — scope narrowed from an all-assigned-brands aggregate to the
 * SINGLE brand selected in the header brand switcher. The page-level
 * per-brand filter is gone, so the header collapses from two rows to one
 * (UX Spec §2) and the switcher is the only control that answers "which
 * brand am I working on?".
 *
 * # Data flow
 *
 * Both sections are DEFERRED props: the `<Deferred>` fallback is the
 * shell's loading skeleton until each resolves on the follow-up round-trip
 * (mirrors the brand home). Candidates sits above submissions, matching the
 * brand-home order. Each section owns its inline Retry.
 *
 * # Isolation (P0)
 *
 * All scoping is server-side (`AgencyHomeController` → `Current.brand`),
 * and per-card action URLs resolve the brand from the record. This page
 * takes no brand input of any kind — there is no prop, no param and no
 * control through which it could widen or redirect scope.
 */
export default function AgencyHome({
  agency_candidates_to_review: candidatesToReview,
  agency_candidates_awaiting_brand: candidatesAwaitingBrand,
  agency_candidates_to_contact: candidatesToContact,
  agency_candidates_to_follow_up: candidatesToFollowUp,
  agency_invites_quiet: invitesQuiet,
  agency_submissions_to_review: submissionsToReview,
}: AgencyHomeProps) {
  const { t } = useTranslation()
  const { currentBrand } = useCurrentContext()

  // OKS-923 — keep the last resolved submissions section so the `<Deferred>`
  // fallback can render the same component type.
  //
  // Keyed on the ACTING BRAND, which is load-bearing here: a brand switch
  // 303s back to this same page (`CurrentBrandController#create`, OKS-854), so
  // this component stays mounted while the new brand's prop resolves. Without
  // the key the previous brand's rows would render under the new brand's
  // header — authorised content, attributed to the wrong brand. On a switch
  // there is nothing legitimate to keep, so the rows are dropped and the
  // skeleton shows.
  const retainedSubmissions = useRetainedDeferredSection(submissionsToReview, {
    identity: currentBrand?.id,
  })

  return (
    <AppLayout>
      <Head title={t("role_home.title")} />
      <div
        className="flex flex-col gap-6 p-4 md:p-6"
        data-testid="agency-home"
        role="region"
        aria-label={t("role_home.title")}
      >
        {/*
          OKS-854 — single-row header. The brand-filter Select and its Label
          were removed with the filter; the heading is all that remains.
         */}
        <header className="flex flex-col gap-4">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("role_home.title")}
          </h1>
        </header>

        {/* Candidates-to-review — scoped to the selected brand. */}
        <Deferred
          data="agency_candidates_to_review"
          fallback={
            <HomeTriageSection
              title={t("candidates.home_review.title")}
              state="loading"
              count={0}
              testId="agency-candidates-to-review"
            />
          }
        >
          {candidatesToReview ? (
            <AgencyCandidatesToReviewSection section={candidatesToReview} />
          ) : null}
        </Deferred>

        {/*
          OKS-950 — "En attente de la marque". Read-only: what this agency has
          sent for validation and is still waiting on. Sits directly below
          candidates-to-review, mirroring the brand home's ordering so the two
          surfaces read the same way round — screen above, waiting below.
        */}
        <Deferred
          data="agency_candidates_awaiting_brand"
          fallback={
            <HomeTriageSection
              title={t("candidates.awaiting_brand.title")}
              state="loading"
              count={0}
              testId="candidates-awaiting-brand"
            />
          }
        >
          {candidatesAwaitingBrand ? (
            <CandidatesAwaitingBrandSection section={candidatesAwaitingBrand} />
          ) : null}
        </Deferred>

        {/*
          OKS-953 (PRD R6) — segments 3, 4 and 5 arrive on the agency home.
          Pipeline order, identical to the brand home, so "the third
          section" means the same thing to both roles (UX Spec §2A).
        */}

        <Deferred
          data="agency_candidates_to_contact"
          fallback={
            <HomeTriageSection
              title={t("candidates.home_to_contact.title")}
              state="loading"
              count={0}
              testId="agency-home-to-contact"
            />
          }
        >
          {candidatesToContact ? (
            <AgencyCandidatesToContactSection section={candidatesToContact} />
          ) : null}
        </Deferred>

        <Deferred
          data="agency_candidates_to_follow_up"
          fallback={
            <HomeTriageSection
              title={t("candidates.home_follow_up.title")}
              state="loading"
              count={0}
              testId="agency-home-follow-up"
            />
          }
        >
          {candidatesToFollowUp ? (
            <AgencyCandidatesToFollowUpSection section={candidatesToFollowUp} />
          ) : null}
        </Deferred>

        <Deferred
          data="agency_invites_quiet"
          fallback={
            <HomeTriageSection
              title={t("candidates.home_invite_stale.title")}
              state="loading"
              count={0}
              testId="agency-home-invite-stale"
            />
          }
        >
          {invitesQuiet ? (
            <AgencyInvitesQuietSection section={invitesQuiet} />
          ) : null}
        </Deferred>

        {/*
          Submissions-to-review — sibling directly below candidates.

          OKS-923 — both branches render THE SAME COMPONENT TYPE once there is
          something to keep, so React reconciles instead of unmounting the
          subtree on each round-trip and the open flag dialog (with the user's
          typed note) survives a correctable failure. See the brand twin for
          the full rationale. The skeleton remains correct for the FIRST
          resolve, when there are no rows to keep.
        */}
        <Deferred
          data="agency_submissions_to_review"
          fallback={
            retainedSubmissions.section ? (
              <AgencySubmissionsToReviewSection
                section={retainedSubmissions.section}
                isRefreshing={retainedSubmissions.isRefreshing}
                refreshFailed={retainedSubmissions.refreshFailed}
              />
            ) : (
              <HomeTriageSection
                title={t("submissions.home_review.title")}
                state="loading"
                count={0}
                testId="agency-submissions-to-review"
              />
            )
          }
        >
          {submissionsToReview ? (
            <AgencySubmissionsToReviewSection section={submissionsToReview} />
          ) : null}
        </Deferred>
      </div>
    </AppLayout>
  )
}
